import React from "react";
import PropTypes from "prop-types";
import VmoResponsiveBar from "../Nivo/VmoResponsiveBar";
import {
  getGridYValuesMultipleBar,
  maxOfGraphMultipleBar,
  maxOfGraphBar,
  getGridYValuesBar,
} from "../../../helper";

const defaultAxisBottom = {
  tickRotation: 0,
  legend: "Time",
  legendOffset: 45,
};

const defaultAxisLeft = {
  tickRotation: 0,
  legend: "Reactions",
  legendOffset: -55,
};

const defaultLegends = {
  anchor: "top",
  direction: "row",
  translateX: 0,
  translateY: -35,
  itemsSpacing: 25,
  itemDirection: "left-to-right",
  itemWidth: 85,
  itemHeight: 23,
};

const ValueOutside = ({ bars }) => {
  return bars.map((bar) => {
    const {
      key,
      width,
      height,
      x,
      y,
      data: { value },
    } = bar;

    return (
      <g key={key} transform={`translate(${x}, ${y})`}>
        <text
          transform={`translate(${width + 10}, ${height / 2 + 3})`}
          textAnchor="middle"
          fontSize="11px"
        >
          {value !== 0 ? value : null}
        </text>
      </g>
    );
  });
};

const VmoBar = ({
  data = [],
  keys = ["value"],
  indexBy = "id",
  margin = { top: 37, right: 27, bottom: 70, left: 60 },
  groupMode = "stacked",
  layout = "vertical",
  colors = { scheme: "nivo" },
  axisLeft: axisLeftObj = {},
  axisBottom: axisBottomObj = {},
  enableLabel = true,
  showLegends = true,
  legends: legendsObj = {},
}) => {
  const axisBottom = { ...defaultAxisBottom, ...axisBottomObj };
  const axisLeft = { ...defaultAxisLeft, ...axisLeftObj };
  const legends = { ...defaultLegends, ...legendsObj };

  let multibar = keys.length > 1;
  if (layout === "horizontal") {
    return (
      <VmoResponsiveBar
        data={data}
        keys={keys}
        indexBy={indexBy}
        margin={margin}
        padding={0.3}
        minValue={0}
        maxValue={
          multibar
            ? maxOfGraphMultipleBar(data, keys, groupMode)
            : maxOfGraphBar(data, keys)
        }
        groupMode={groupMode}
        layout="horizontal"
        valueScale={{ type: "linear" }}
        indexScale={{ type: "band", round: true }}
        colors={colors}
        borderColor={{
          from: "color",
          modifiers: [["darker", 1.6]],
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickValues: multibar
            ? getGridYValuesMultipleBar(data, keys, groupMode)
            : getGridYValuesBar(data, keys),
          tickSize: 5,
          tickPadding: 5,
          tickRotation: axisBottom.tickRotation,
          legend: axisBottom.legend,
          legendPosition: "middle",
          legendOffset: axisBottom.legendOffset,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: axisLeft.tickRotation,
          legend: axisLeft.legend,
          legendPosition: "middle",
          legendOffset: axisLeft.legendOffset,
        }}
        enableGridX
        gridXValues={
          multibar
            ? getGridYValuesMultipleBar(data, keys, groupMode)
            : getGridYValuesBar(data, keys)
        }
        enableGridY={false}
        gridYValues={
          multibar
            ? getGridYValuesMultipleBar(data, keys, groupMode)
            : getGridYValuesBar(data, keys)
        }
        enableLabel={false}
        labelSkipWidth={18}
        labelSkipHeight={12}
        labelTextColor={{
          from: "color",
          modifiers: [["darker", 1.6]],
        }}
        layers={[
          "grid",
          "axes",
          "bars",
          "markers",
          "legends",
          "annotations",
          (props) => <ValueOutside {...props} />,
        ]}
        legends={[
          ...(showLegends
            ? [
                {
                  dataFrom: "keys",
                  anchor: legends.anchor,
                  direction: legends.direction,
                  justify: false,
                  translateX: legends.translateX,
                  translateY: legends.translateY,
                  itemsSpacing: legends.itemsSpacing,
                  itemDirection: legends.itemDirection,
                  itemWidth: legends.itemWidth,
                  itemHeight: legends.itemHeight,
                  itemOpacity: 0.75,
                  symbolSize: 13,
                  symbolShape: "circle",
                  symbolBorderColor: "rgba(0, 0, 0, .5)",
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemTextColor: "#000",
                      },
                    },
                  ],
                },
              ]
            : []),
        ]}
      />
    );
  }
  return (
    <VmoResponsiveBar
      data={data}
      keys={keys}
      indexBy={indexBy}
      margin={margin}
      padding={0.3}
      minValue={0}
      maxValue={
        multibar
          ? maxOfGraphMultipleBar(data, keys, groupMode)
          : maxOfGraphBar(data, keys)
      }
      groupMode={groupMode}
      layout={layout}
      valueScale={{ type: "linear" }}
      indexScale={{ type: "band", round: true }}
      colors={colors}
      borderColor={{
        from: "color",
        modifiers: [["darker", 1.6]],
      }}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: axisBottom.tickRotation,
        legend: axisBottom.legend,
        legendPosition: "middle",
        legendOffset: axisBottom.legendOffset,
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: axisLeft.tickRotation,
        tickValues: multibar
          ? getGridYValuesMultipleBar(data, keys, groupMode)
          : getGridYValuesBar(data, keys),
        legend: axisLeft.legend,
        legendPosition: "middle",
        legendOffset: axisLeft.legendOffset,
      }}
      enableGridX={false}
      enableGridY
      gridYValues={
        multibar
          ? getGridYValuesMultipleBar(data, keys, groupMode)
          : getGridYValuesBar(data, keys)
      }
      enableLabel={enableLabel}
      label={(d) => (
        <tspan style={{ fill: "black" }} y={-8}>
          {d.value}
        </tspan>
      )}
      labelSkipWidth={12}
      labelSkipHeight={20}
      labelTextColor={{
        from: "color",
        modifiers: [["darker", 1.6]],
      }}
      legends={[
        ...(showLegends
          ? [
              {
                dataFrom: "keys",
                anchor: legends.anchor,
                direction: legends.direction,
                justify: false,
                translateX: legends.translateX,
                translateY: legends.translateY,
                itemsSpacing: legends.itemsSpacing,
                itemDirection: legends.itemDirection,
                itemWidth: legends.itemWidth,
                itemHeight: legends.itemHeight,
                itemOpacity: 0.75,
                symbolSize: 13,
                symbolShape: "circle",
                symbolBorderColor: "rgba(0, 0, 0, .5)",
                effects: [
                  {
                    on: "hover",
                    style: {
                      itemTextColor: "#000",
                    },
                  },
                ],
              },
            ]
          : []),
      ]}
    />
  );
};

export default VmoBar;

VmoBar.propTypes = {
  data: PropTypes.array.isRequired,
  keys: PropTypes.arrayOf(PropTypes.string),
  indexBy: PropTypes.string,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  groupMode: PropTypes.string,
  layout: PropTypes.string,
  colors: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.object,
  ]),
  axisBottom: PropTypes.shape({
    tickRotation: PropTypes.number,
    legend: PropTypes.string,
    legendOffset: PropTypes.number,
  }),
  axisLeft: PropTypes.shape({
    tickRotation: PropTypes.number,
    legend: PropTypes.string,
    legendOffset: PropTypes.number,
  }),
  enableLabel: PropTypes.bool,
  showLegends: PropTypes.bool,
  legends: PropTypes.shape({
    anchor: PropTypes.string,
    direction: PropTypes.string,
    translateX: PropTypes.number,
    translateY: PropTypes.number,
    itemsSpacing: PropTypes.number,
    itemDirection: PropTypes.string,
    itemWidth: PropTypes.number,
    itemHeight: PropTypes.number,
  }),
};

VmoBar.defaultProps = {
  data: [],
  keys: ["value"],
  indexBy: "id",
  margin: {
    top: 37,
    right: 27,
    bottom: 70,
    left: 60,
  },
  groupMode: "stacked",
  layout: "vertical",
  colors: { scheme: "nivo" },
  axisBottom: {
    tickRotation: 0,
    legend: "Time",
    legendOffset: 45,
  },
  axisLeft: {
    tickRotation: 0,
    legend: "Reactions",
    legendOffset: -55,
  },
  enableLabel: true,
  showLegends: true,
  legends: {
    anchor: "top",
    direction: "row",
    translateX: 0,
    translateY: -35,
    itemsSpacing: 25,
    itemDirection: "left-to-right",
    itemWidth: 85,
    itemHeight: 23,
  },
};
